import classNames from "classnames";
import { objectKeys } from "app/helpers/objects";
import type { ReactNode } from "react";
import { useState, type JSX } from "react";
import { INITIAL_HOVERED_ROW_INDEX, HOVERED_ROW_CLASSNAME, CAN_BE_HOVERED_ROW_CLASSNAME } from "../constants";
import { StyledTableHeaderCell, StyledTableCell } from "../TableCell/styles";
import type { TableItem, CustomRenderers, TableColumn, CustomHeaderRenderers } from "../types";
import { StyledTable, StyledTableHeader, StyledTableBody, StyledTableColumn, StyledWrapperTableBody } from "./styles";

type TableProps<T extends TableItem> = {
    data: ReadonlyArray<T>;
    columns: TableColumn<T>;
    customRenderers?: CustomRenderers<T>;
    customHeaderRenderers?: CustomHeaderRenderers<TableColumn<T>>;
    reactsToMouseEnter?: boolean;
    hasStickyHeader?: boolean;
    emptyState?: JSX.Element;
    className?: string;
};

const Table = <T extends TableItem>(props: TableProps<T>) => {
    const {
        data,
        columns,
        customRenderers,
        customHeaderRenderers,
        reactsToMouseEnter = false,
        hasStickyHeader = false,
        className = null,
        emptyState,
    } = props;
    const [hoveredRowIndex, setHoveredRowIndex] = useState(INITIAL_HOVERED_ROW_INDEX);
    const columnKeys = objectKeys(columns);

    function resetHoveredRowIndex() {
        setHoveredRowIndex(INITIAL_HOVERED_ROW_INDEX);
    }

    function getMouseHandlers(index: number) {
        if (reactsToMouseEnter) {
            return {
                onMouseLeave: resetHoveredRowIndex,
                onMouseEnter: () => setHoveredRowIndex(index),
            };
        }

        return {};
    }

    function renderCell(key: keyof T, entry: T, index: number) {
        const isHovered = hoveredRowIndex === index;
        const customRenderer = customRenderers?.[key];
        const cellContent = customRenderer ? customRenderer(entry, index, isHovered) : entry[key];

        return (
            <StyledTableCell
                key={`col-${String(key)}-row-${index}`}
                className={classNames({
                    [CAN_BE_HOVERED_ROW_CLASSNAME]: reactsToMouseEnter,
                    [HOVERED_ROW_CLASSNAME]: isHovered,
                })}
                {...getMouseHandlers(index)}
            >
                {cellContent as ReactNode}
            </StyledTableCell>
        );
    }

    function renderHeaderCell(key: keyof TableColumn<T>) {
        const customRenderer = customHeaderRenderers?.[key];

        if (customRenderer) {
            return customRenderer();
        }

        return (
            <StyledTableHeaderCell
                data-automation="header-cell"
                data-automation-column-key={key}
            >
                {columns[key].text}
            </StyledTableHeaderCell>
        );
    }

    return (
        <StyledTable
            data-automation="table"
            className={className}
        >
            <StyledTableHeader isSticky={hasStickyHeader}>
                {columnKeys.map((key, i, array) => {
                    const column = columns[key];

                    if (typeof column === "undefined") {
                        return null;
                    }

                    return (
                        <StyledTableColumn
                            key={`head-${i}`}
                            length={array.length}
                            size={column.size}
                            align={column.align}
                        >
                            {renderHeaderCell(key)}
                        </StyledTableColumn>
                    );
                })}
            </StyledTableHeader>
            <StyledWrapperTableBody>
                <StyledTableBody>
                    {data.length > 0 &&
                        columnKeys.map((key, _i, array) => {
                            const column = columns[key];

                            if (typeof column === "undefined") {
                                return null;
                            }

                            return (
                                <StyledTableColumn
                                    key={`col-${String(key)}`}
                                    length={array.length}
                                    size={column.size}
                                    align={column.align}
                                >
                                    {data.map((entry, index) => renderCell(key, entry, index))}
                                </StyledTableColumn>
                            );
                        })}
                    {data.length === 0 && emptyState}
                </StyledTableBody>
            </StyledWrapperTableBody>
        </StyledTable>
    );
};

export default Table;
